import { Alert, Box, Button } from '@mui/material';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { User } from 'components/types';

type Props = {
  user: User | null;
  isTrialExpired?: boolean;
};

type AccountStatusAlertConfig = {
  show: boolean;
  severity: 'warning' | 'error';
  nonOwnerMessage: ReactNode;
  ownerMessage: string;
  buttonLabel: string;
  buttonColor: 'warning' | 'error';
};

/**
 * Account-wide status alerts shown in the top bar (trial expired / read-only after failed payment).
 */
const AccountStatusAlerts = ({ user, isTrialExpired }: Props) => {
  const router = useRouter();

  const renderAccountStatusAlert = ({
    show,
    severity,
    nonOwnerMessage,
    ownerMessage,
    buttonLabel,
    buttonColor,
  }: AccountStatusAlertConfig) => {
    if (!show || !user?.account) {
      return null;
    }

    let content = <span>{nonOwnerMessage}</span>;

    if (user.role === 'superAdmin') {
      content = (
        <>
          <Box component="span" sx={{ marginRight: '5px' }}>
            {ownerMessage}
          </Box>
          <Button
            variant="contained"
            color={buttonColor}
            size="small"
            sx={{ textTransform: 'none' }}
            onClick={() => {
              void router.push('/settings/billing');
            }}
          >
            {buttonLabel}
          </Button>
        </>
      );
    }

    return (
      <Alert severity={severity} icon={false} sx={{ padding: '0px', paddingRight: '5px', paddingLeft: '5px' }}>
        {content}
      </Alert>
    );
  };

  return (
    <>
      {renderAccountStatusAlert({
        show: Boolean(isTrialExpired && user?.account),
        severity: 'warning',
        nonOwnerMessage: (
          <>
            Your agency account requires an upgrade to a paid plan. Please contact your agency account owner{' '}
            {user?.account?.ownerName} <strong>({user?.account?.ownerEmail})</strong>.
          </>
        ),
        ownerMessage: 'Your trial has expired. Please upgrade to a paid plan to continue using the platform.',
        buttonLabel: 'Upgrade',
        buttonColor: 'warning',
      })}
      {renderAccountStatusAlert({
        show: Boolean(user?.isReadOnly),
        severity: 'error',
        nonOwnerMessage: (
          <>
            Your agency account is in read-only mode because of a failed payment. Please contact your agency account
            owner {user?.account?.ownerName} <strong>({user?.account?.ownerEmail})</strong> to update the payment
            method.
          </>
        ),
        ownerMessage:
          'Payment failed — your account is in read-only mode. Update your payment method to restore full access.',
        buttonLabel: 'Fix payment',
        buttonColor: 'error',
      })}
    </>
  );
};

export default AccountStatusAlerts;
